import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa6";
import Cookies from "js-cookie";

// Context & Services
import AppContext from "../../context/AppContext";
import { getCallCenterApiErrorMessage } from "../../service/CallCenterApi";
import {
  createCcWorkspaceUser,
  fetchCcSecurityRoles,
  getCcWorkspaceUser,
  patchCcWorkspaceUser,
  resetCcWorkspaceUserPassword,
} from "../../service/CallCenterUserAdmin";

// Components
import translations from "../../components/common/Translations";
import ReactSelector from "../../components/common/CommonReactSelector";
import BackButton from "../../components/common/BackButton";
import CommonButton from "../../components/common/CommonButton";

const CC_ROLE_FALLBACK_OPTIONS = [
  { value: "SUPER_ADMIN", label: "Super Admin" },
  { value: "CALL_ADMIN", label: "Call Admin" },
  { value: "SUPERVISOR", label: "Supervisor" },
  { value: "CALL_AGENT", label: "Call Agent" },
];

const AddUser = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!userId;
  
  const { theme, langMode } = useContext(AppContext);
  
  const [isLoader, setLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [auditMeta, setAuditMeta] = useState({ createdAt: "", updatedAt: "" });
  const baselineRef = useRef(null);
  const [initialValues, setInitialValues] = useState({
    name: "",
    email: "",
    password: "",
    role: "",
    active: true,
    agentExtension: "",
    receiveDailyReport: false,
  });

  // Common height variable for consistency
  const inputHeight = "h-[50px]";

  // Custom styles for ReactSelector to match the input height
  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      height: "50px",
      minHeight: "50px",
      borderRadius: "0.375rem", // rounded-md
      border: "1px solid #D1D5DB", // gray-300
    }),
  };

  useEffect(() => {
    let cancelled = false;
    fetchCcSecurityRoles()
      .then((roles) => {
        if (cancelled || !Array.isArray(roles)) return;
        const opts = roles
          .filter((r) => r?.assignableByCurrentUser === true)
          .map((r) => ({
            value: r.role,
            label: r.displayName || r.role,
          }));
        setRoleOptions(opts.length ? opts : CC_ROLE_FALLBACK_OPTIONS);
      })
      .catch(() => {
        if (!cancelled) setRoleOptions(CC_ROLE_FALLBACK_OPTIONS);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().required("Name is required"),
        email: Yup.string().email("Invalid email").required("Email is required"),
        password: isEditMode
          ? Yup.string()
              .nullable()
              .transform((v) => (v === "" || v == null ? undefined : v))
              .test(
                "min-if-set",
                "Password must be at least 8 characters",
                (v) => v == null || String(v).length >= 8
              )
          : Yup.string().min(8, "Password must be at least 8 characters").required("Password is required"),
        role: Yup.string().required("Role is required"),
        agentExtension: Yup.string(),
      }),
    [isEditMode]
  );

  useEffect(() => {
    if (isEditMode) {
      setLoader(true);
      getCcWorkspaceUser(userId)
        .then((user) => {
          const next = {
            name: user.name || "",
            email: user.email || "",
            password: "",
            role: user.role || "",
            active: user.active !== false,
            agentExtension: user.agentExtension || "",
            receiveDailyReport: Boolean(user.receiveDailyReport),
          };
          baselineRef.current = { ...next };
          setAuditMeta({
            createdAt: user.createdAt || "",
            updatedAt: user.updatedAt || "",
          });
          setInitialValues(next);
        })
        .catch((error) => {
          console.error("Error fetching user:", error);
          const envelope = error?.envelope ?? error?.response?.data;
          toast.error(
            envelope != null ? getCallCenterApiErrorMessage(envelope, "Failed to load user data") : "Failed to load user data"
          );
        })
        .finally(() => setLoader(false));
    }
  }, [userId, isEditMode]);

  useEffect(() => {
    if (!isEditMode) {
      baselineRef.current = null;
      setAuditMeta({ createdAt: "", updatedAt: "" });
    }
  }, [isEditMode]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoader(true);
        const cookieUserId = Cookies.get("userId");
        const isSelf =
          isEditMode && cookieUserId != null && String(cookieUserId) === String(userId);

        if (isEditMode) {
          if (isSelf && values.active === false) {
            toast.error("You cannot deactivate your own account.");
            return;
          }

          const b = baselineRef.current;
          const trim = (s) => String(s ?? "").trim();
          const body = {};
          if (b) {
            if (trim(values.name) !== trim(b.name)) body.name = trim(values.name);
            if (trim(values.email) !== trim(b.email)) body.email = trim(values.email);
            if (values.role !== b.role) body.role = values.role;
            if (values.active !== b.active) body.active = values.active;
            const ext = trim(values.agentExtension);
            const extB = trim(b.agentExtension);
            if (ext !== extB) body.agentExtension = ext;
            if (!!values.receiveDailyReport !== !!b.receiveDailyReport) {
              body.receiveDailyReport = values.receiveDailyReport;
            }
          } else {
            Object.assign(body, {
              name: trim(values.name),
              email: trim(values.email),
              role: values.role,
              active: values.active,
              agentExtension: trim(values.agentExtension),
              receiveDailyReport: values.receiveDailyReport,
            });
          }

          const pw = values.password?.trim();
          if (Object.keys(body).length > 0) {
            await patchCcWorkspaceUser(userId, body);
          }
          if (pw) {
            await resetCcWorkspaceUserPassword(userId, pw);
          }
          if (Object.keys(body).length === 0 && !pw) {
            toast.info("No changes to save.");
            return;
          }
        } else {
          await createCcWorkspaceUser({
            name: values.name.trim(),
            email: values.email.trim(),
            password: values.password,
            role: values.role,
            agentExtension: values.agentExtension?.trim() || undefined,
            receiveDailyReport: values.receiveDailyReport,
          });
        }
        toast.success(isEditMode ? "User updated successfully" : "User created successfully");
        navigate("/all-users");
      } catch (error) {
        const envelope = error?.envelope ?? error?.response?.data;
        toast.error(
          envelope != null ? getCallCenterApiErrorMessage(envelope, "An error occurred. Please try again.") : "An error occurred. Please try again."
        );
      } finally {
        setLoader(false);
      }
    },
  });

  const ErrorMsg = ({ name }) => (
    formik.touched[name] && formik.errors[name] ? (
      <span className="text-red-500 text-xs mt-1">{formik.errors[name]}</span>
    ) : null
  );

  const isSelfEdit =
    isEditMode &&
    Cookies.get("userId") != null &&
    String(Cookies.get("userId")) === String(userId);
  const fieldDisabled = isLoader;

  return (
    <div className="w-full max-h-[93vh] h-full bg-white flex flex-col gap-[38px]">
      <BackButton 
        path={'/all-users'} 
        pagename={isEditMode ? "Edit User" : translations[langMode].addUser} 
      />

      <div className={`w-[75%] mx-auto ${theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""}`}>
        <div className="w-full">
          <form onSubmit={formik.handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Name */}
              <div className="flex flex-col gap-2">
                <label className="font-medium text-gray-700">Name *</label>
                <input
                  type="text"
                  {...formik.getFieldProps("name")}
                  disabled={fieldDisabled}
                  className={`border px-3 rounded-md focus:ring-2 focus:ring-blue-500 outline-none ${inputHeight} ${formik.touched.name && formik.errors.name ? 'border-red-500' : 'border-gray-300'} disabled:bg-gray-100 disabled:cursor-not-allowed`}
                />
                <ErrorMsg name="name" />
              </div>

              {/* Email */}
              <div className="flex flex-col gap-2">
                <label className="font-medium text-gray-700">Email *</label>
                <input
                  type="email"
                  {...formik.getFieldProps("email")}
                  disabled={fieldDisabled}
                  className={`border px-3 rounded-md focus:ring-2 focus:ring-blue-500 outline-none ${inputHeight} ${formik.touched.email && formik.errors.email ? 'border-red-500' : 'border-gray-300'} disabled:bg-gray-100 disabled:cursor-not-allowed`}
                />
                <ErrorMsg name="email" />
              </div>

              {/* Password */}
              <div className="flex flex-col gap-2 relative">
                <label className="font-medium text-gray-700">
                  Password {isEditMode ? "(Leave blank)" : "*"}
                </label>
                <div className={`relative ${inputHeight}`}>
                  <input
                    type={showPassword ? "text" : "password"}
                    {...formik.getFieldProps("password")}
                    disabled={fieldDisabled}
                    className={`w-full h-full border px-3 rounded-md focus:ring-2 focus:ring-blue-500 outline-none ${formik.touched.password && formik.errors.password ? 'border-red-500' : 'border-gray-300'} disabled:bg-gray-100 disabled:cursor-not-allowed`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showPassword ? <FaEye size={18} /> : <FaEyeSlash size={18} />}
                  </button>
                </div>
                <ErrorMsg name="password" />
              </div>

              {/* Role Selector */}
              <div className="flex flex-col gap-2">
                <label className="font-medium text-gray-700">Role *</label>
                <div className={inputHeight}>
                    <ReactSelector
                        options={roleOptions.length ? roleOptions : CC_ROLE_FALLBACK_OPTIONS}
                        styles={customSelectStyles}
                        isDisabled={fieldDisabled}
                        value={(roleOptions.length ? roleOptions : CC_ROLE_FALLBACK_OPTIONS).find((item) => item.value === formik.values.role)}
                        onChange={(e) => formik.setFieldValue("role", e?.value)}
                    />
                </div>
                <ErrorMsg name="role" />
              </div>

              {/* Agent Extension */}
              <div className="flex flex-col gap-2">
                <label className="font-medium text-gray-700">Agent extension</label>
                <input
                  type="text"
                  {...formik.getFieldProps("agentExtension")}
                  disabled={fieldDisabled}
                  className={`border px-3 rounded-md focus:ring-2 focus:ring-blue-500 outline-none ${inputHeight} ${formik.touched.agentExtension && formik.errors.agentExtension ? 'border-red-500' : 'border-gray-300'} disabled:bg-gray-100 disabled:cursor-not-allowed`}
                />
                <ErrorMsg name="agentExtension" />
              </div>

              {/* Checkboxes Group */}
              <div className={`flex flex-col justify-center gap-4 ${inputHeight} mt-auto`}>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="active"
                    className="w-5 h-5 cursor-pointer"
                    disabled={fieldDisabled}
                    checked={formik.values.active}
                    onChange={(e) => {
                      if (isSelfEdit && !e.target.checked) {
                        toast.error("You cannot deactivate your own account.");
                        return;
                      }
                      formik.setFieldValue("active", e.target.checked);
                    }}
                  />
                  <label htmlFor="active" className="cursor-pointer font-medium text-gray-700">Active Status</label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="report"
                    className="w-5 h-5 cursor-pointer"
                    disabled={fieldDisabled}
                    checked={formik.values.receiveDailyReport}
                    onChange={(e) => formik.setFieldValue("receiveDailyReport", e.target.checked)}
                  />
                  <label htmlFor="report" className="cursor-pointer font-medium text-gray-700">Receive Daily Report</label>
                </div>
              </div>
            </div>

            {isEditMode && (auditMeta.createdAt || auditMeta.updatedAt) && (
              <div className="text-sm text-gray-500 flex flex-wrap gap-6 px-1">
                {auditMeta.createdAt ? (
                  <span>Created: {new Date(auditMeta.createdAt).toLocaleString()}</span>
                ) : null}
                {auditMeta.updatedAt ? (
                  <span>Last updated: {new Date(auditMeta.updatedAt).toLocaleString()}</span>
                ) : null}
              </div>
            )}

            <div className="flex justify-center gap-4 pt-6 border-t">
              <CommonButton variant="danger" type="button" onClick={() => navigate("/all-users")}>
                Cancel
              </CommonButton>
              <CommonButton type="submit" variant="primary" disabled={isLoader}>
                {isLoader ? "Processing..." : isEditMode ? "Update User" : "Save User"}
              </CommonButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddUser;